import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '../api/users';
import { useAuthStore } from '../store/authStore';

/**
 * Lineup Help — "which of my players play on a given date, in every league".
 *
 * Everything on this page is fetched straight from the public Sleeper API in
 * the browser (Sleeper sends `Access-Control-Allow-Origin: *`). That keeps
 * the backend — and its shared Sleeper rate-limit budget — out of the loop,
 * and means the data is as fresh as Sleeper itself: we refetch on every
 * visit and on demand via the Refresh button. The last successful pull is
 * cached in localStorage so the page paints instantly while the refresh
 * runs, and the (large) player database is cached for a day.
 *
 * Only the verified owner of the Sleeper account can open it; the route is
 * gated on `account.user_id === user.id && account.is_verified`.
 */

const SLEEPER = 'https://api.sleeper.app';
const META_TTL_MS = 24 * 60 * 60 * 1000;

interface RosterEntry {
  players: string[];
  starters: string[];
  reserve: string[];
  taxi: string[];
}

interface LeagueEntry {
  league_id: string;
  name: string;
  roster_positions: string[];
  roster: RosterEntry | null;
}

interface PlayerMeta {
  name: string;
  pos: string;
  team: string | null;
  inj: string | null;
  status: string | null;
}

interface Game {
  date: string;
  week: number | null;
  home: string;
  away: string;
  status: string | null;
}

interface LineupData {
  season: string;
  leagues: LeagueEntry[];
  meta: Record<string, PlayerMeta>;
  schedule: Game[];
  fetchedAt: number;
}

type PlayerKind = 'start' | 'bench' | 'taxi' | 'ir';

interface PlayingPlayer {
  pid: string;
  kind: PlayerKind;
  slot: string;
  order: number;
  m: PlayerMeta;
}

/* ---------- localStorage helpers (best-effort; never throw) ---------- */
function lsGet<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}
function lsSet(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota / private mode — ignore */
  }
}

/* ---------- date helpers (local calendar dates, YYYY-MM-DD) ---------- */
function isoLocal(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  );
}
function shiftDate(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return isoLocal(new Date(y, m - 1, d + days));
}
function prettyDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/* ---------- Sleeper fetch helpers ---------- */
async function fetchJSON<T>(url: string): Promise<T> {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
  return r.json() as Promise<T>;
}

async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let i = 0;
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (i < items.length) {
        const idx = i++;
        out[idx] = await fn(items[idx]);
      }
    })
  );
  return out;
}

interface SleeperLeague {
  league_id: string;
  name?: string;
  roster_positions?: string[];
}
interface SleeperRoster {
  owner_id: string | null;
  co_owners: string[] | null;
  players: string[] | null;
  starters: string[] | null;
  reserve: string[] | null;
  taxi: string[] | null;
}
interface SleeperPlayer {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  position?: string;
  team?: string | null;
  injury_status?: string | null;
  status?: string | null;
}

/**
 * Pull everything the page needs for one Sleeper user. `onStatus` receives
 * human-readable progress so the UI can show what's happening — the roster
 * fan-out and the player DB download can take several seconds.
 */
async function fetchLineupData(
  uid: string,
  onStatus: (s: string) => void
): Promise<LineupData> {
  onStatus('Fetching leagues…');
  const state = await fetchJSON<{ league_season: string }>(`${SLEEPER}/v1/state/nfl`);
  const season = state.league_season;
  const leagues = await fetchJSON<SleeperLeague[]>(
    `${SLEEPER}/v1/user/${uid}/leagues/nfl/${season}`
  );

  let done = 0;
  onStatus(`Fetching rosters (0/${leagues.length})…`);
  const entries = await mapLimit(leagues, 8, async (lg): Promise<LeagueEntry> => {
    const rosters = await fetchJSON<SleeperRoster[]>(
      `${SLEEPER}/v1/league/${lg.league_id}/rosters`
    );
    onStatus(`Fetching rosters (${++done}/${leagues.length})…`);
    const mine = rosters.find(
      (r) => r.owner_id === uid || (r.co_owners && r.co_owners.includes(uid))
    );
    return {
      league_id: lg.league_id,
      name: lg.name || lg.league_id,
      roster_positions: lg.roster_positions || [],
      roster: mine
        ? {
            players: mine.players || [],
            starters: mine.starters || [],
            reserve: mine.reserve || [],
            taxi: mine.taxi || [],
          }
        : null,
    };
  });

  // Player meta: cached per-user for a day (injury / team changes), and
  // re-pulled sooner if a rostered player we've never seen shows up.
  const metaKey = `lineupHelp.meta.${uid}`;
  const ids = [...new Set(entries.flatMap((e) => (e.roster ? e.roster.players : [])))];
  const cache = lsGet<{ fetchedAt: number; players: Record<string, PlayerMeta> }>(metaKey);
  let meta: Record<string, PlayerMeta> = { ...(cache?.players ?? {}) };
  const stale = !cache || !cache.fetchedAt || Date.now() - cache.fetchedAt > META_TTL_MS;
  const unknown = ids.filter((pid) => !meta[pid]);
  if (stale || unknown.length) {
    onStatus(
      `Fetching player database (${stale ? 'daily update' : `${unknown.length} new players`})…`
    );
    const db = await fetchJSON<Record<string, SleeperPlayer>>(`${SLEEPER}/v1/players/nfl`);
    const fresh: Record<string, PlayerMeta> = {};
    for (const pid of ids) {
      const p = db[pid] || {};
      fresh[pid] = {
        name:
          p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || pid,
        pos: p.position || '?',
        team: p.team || null,
        inj: p.injury_status || null,
        status: p.status || null,
      };
    }
    meta = { ...meta, ...fresh };
    lsSet(metaKey, { fetchedAt: Date.now(), players: fresh });
  }

  onStatus('Fetching NFL schedule…');
  const schedule = (
    await fetchJSON<Array<Record<string, unknown>>>(
      `${SLEEPER}/schedule/nfl/regular/${season}`
    )
  ).map(
    (g): Game => ({
      date: String(g.date ?? ''),
      week: typeof g.week === 'number' ? g.week : null,
      home: String(g.home ?? ''),
      away: String(g.away ?? ''),
      status: g.status == null ? null : String(g.status),
    })
  );

  return { season, leagues: entries, meta, schedule, fetchedAt: Date.now() };
}

/* ---------- lineup derivation ---------- */
const SLOT_LABEL: Record<string, string> = {
  SUPER_FLEX: 'SFLX',
  REC_FLEX: 'RFLX',
  WRRB_FLEX: 'W/R',
  IDP_FLEX: 'IDP',
};
const slotName = (s: string) => SLOT_LABEL[s] || s;

function playersFor(
  lg: LeagueEntry,
  teams: Set<string>,
  meta: Record<string, PlayerMeta>
): PlayingPlayer[] {
  const r = lg.roster;
  if (!r) return [];
  const out: Omit<PlayingPlayer, 'm'>[] = [];
  const seen = new Set<string>();
  r.starters.forEach((pid, i) => {
    if (!pid || pid === '0') return;
    seen.add(pid);
    out.push({ pid, kind: 'start', slot: slotName(lg.roster_positions[i] || 'ST'), order: i });
  });
  for (const pid of r.players) {
    if (seen.has(pid)) continue;
    seen.add(pid);
    const kind: PlayerKind = r.reserve.includes(pid)
      ? 'ir'
      : r.taxi.includes(pid)
        ? 'taxi'
        : 'bench';
    out.push({
      pid,
      kind,
      slot: kind === 'ir' ? 'IR' : kind === 'taxi' ? 'TAXI' : 'BN',
      order: kind === 'bench' ? 100 : kind === 'taxi' ? 200 : 300,
    });
  }
  return out
    .map((p) => ({
      ...p,
      m: meta[p.pid] || { name: p.pid, pos: '?', team: null, inj: null, status: null },
    }))
    .filter((p) => p.m.team && teams.has(p.m.team))
    .sort((a, b) => a.order - b.order || a.m.name.localeCompare(b.m.name));
}

function emptyStarterSlots(lg: LeagueEntry): number {
  const r = lg.roster;
  if (!r) return 0;
  const n = lg.roster_positions.filter((s) => s !== 'BN' && s !== 'IR' && s !== 'TAXI').length;
  const filled = r.starters.filter((pid) => pid && pid !== '0').length;
  return Math.max(0, n - filled);
}

function injAbbr(s: string): string {
  return (
    {
      Questionable: 'Q',
      Doubtful: 'D',
      Out: 'OUT',
      IR: 'IR',
      PUP: 'PUP',
      Sus: 'SUS',
      NA: 'NA',
      COV: 'COV',
      DNR: 'DNR',
    }[s] || s
  );
}

/* ---------- small presentational bits ---------- */
function PosChip({ pos, dim }: { pos: string; dim?: boolean }) {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const palette: Record<string, [string, string]> = dark
    ? {
        QB: ['#4a2033', '#f096be'],
        RB: ['#17352a', '#6fd0a2'],
        WR: ['#1e2f4d', '#8ab4f5'],
        TE: ['#40301a', '#eab273'],
      }
    : {
        QB: ['#f8d7e3', '#a61e5c'],
        RB: ['#d4f0e0', '#14734a'],
        WR: ['#d8e6fb', '#1d4fa8'],
        TE: ['#fde8cf', '#a05a10'],
      };
  const [bg, fg] = palette[pos] || [theme.palette.action.selected, theme.palette.text.secondary];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 30,
        textAlign: 'center',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        fontSize: '0.72rem',
        fontWeight: 700,
        bgcolor: bg,
        color: fg,
        opacity: dim ? 0.7 : 1,
      }}
    >
      {pos}
    </Box>
  );
}

function SlotChip({ slot, kind }: { slot: string; kind: PlayerKind }) {
  const theme = useTheme();
  const styles =
    kind === 'start'
      ? { bgcolor: theme.palette.primary.main + '22', color: theme.palette.primary.main }
      : kind === 'bench'
        ? { bgcolor: theme.palette.action.selected, color: theme.palette.text.secondary }
        : { bgcolor: theme.palette.error.main + '22', color: theme.palette.error.main };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        minWidth: 44,
        textAlign: 'center',
        px: 0.75,
        py: 0.25,
        borderRadius: 1,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.03em',
        ...styles,
      }}
    >
      {slot}
    </Box>
  );
}

function Kpi({ value, label }: { value: number; label: string }) {
  return (
    <Card variant="outlined" sx={{ px: 1.75, py: 1, minWidth: 120 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
      >
        {label}
      </Typography>
    </Card>
  );
}

/* ---------- the page ---------- */
export default function LineupHelpPage() {
  const { sleeperUserId } = useParams<{ sleeperUserId: string }>();
  const { user } = useAuthStore();
  const theme = useTheme();

  // Ownership check goes through our own backend so a user can't open
  // someone else's lineups just by editing the URL.
  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['sleeperProfile', sleeperUserId],
    queryFn: () => usersApi.getSleeperProfile(sleeperUserId!),
    enabled: !!sleeperUserId,
  });
  const account = profile?.account;
  const isOwner = !!account && !!user && account.user_id === user.id && account.is_verified;

  const snapshotKey = `lineupHelp.snapshot.${sleeperUserId}`;
  const hiddenKey = `lineupHelp.hidden.${sleeperUserId}`;

  const [data, setData] = useState<LineupData | null>(() => lsGet<LineupData>(snapshotKey));
  const [hidden, setHidden] = useState<Set<string>>(() => {
    const saved = lsGet<string[]>(hiddenKey);
    return new Set(Array.isArray(saved) ? saved : []);
  });
  const [hiddenInitialised, setHiddenInitialised] = useState(
    () => Array.isArray(lsGet<string[]>(hiddenKey))
  );
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState<string>('');
  const [statusIsError, setStatusIsError] = useState(false);
  const [tab, setTab] = useState<0 | 1>(0);
  const [date, setDate] = useState<string>(() => isoLocal(new Date()));
  const [hideEmpty, setHideEmpty] = useState(false);
  const refreshInFlight = useRef(false);

  const persistHidden = useCallback(
    (next: Set<string>) => {
      setHidden(next);
      setHiddenInitialised(true);
      lsSet(hiddenKey, [...next]);
    },
    [hiddenKey]
  );

  const refresh = useCallback(async () => {
    if (!sleeperUserId || refreshInFlight.current) return;
    refreshInFlight.current = true;
    setRefreshing(true);
    setStatusIsError(false);
    try {
      const fresh = await fetchLineupData(sleeperUserId, setStatus);
      setData(fresh);
      lsSet(snapshotKey, fresh);
      // First visit: hide leagues where the user has no roster (separator
      // "leagues", leagues they only commission, etc.). After that, respect
      // whatever they've ticked on the Leagues tab.
      if (!hiddenInitialised) {
        persistHidden(
          new Set(fresh.leagues.filter((l) => !l.roster).map((l) => l.league_id))
        );
      }
      setStatus('Up to date.');
    } catch (e) {
      setStatusIsError(true);
      setStatus(
        `Refresh failed${data ? ', showing last saved data' : ''}: ${
          e instanceof Error ? e.message : String(e)
        }`
      );
    } finally {
      setRefreshing(false);
      refreshInFlight.current = false;
    }
    // `data` is intentionally omitted: it's only read for the error message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sleeperUserId, snapshotKey, hiddenInitialised, persistHidden]);

  // Always fresh: refetch as soon as the page opens for the verified owner.
  useEffect(() => {
    if (isOwner) void refresh();
    // Only re-run when ownership flips true (initial load) — not on every
    // change of `refresh`'s identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwner, sleeperUserId]);

  /* ----- derived view model ----- */
  const view = useMemo(() => {
    if (!data) return null;
    const games = data.schedule.filter((g) => g.date === date && g.status !== 'canceled');
    const teams = new Set(games.flatMap((g) => [g.home, g.away]));
    const week = games.length ? games[0].week : null;
    const rows: Array<{ lg: LeagueEntry; ps: PlayingPlayer[]; st: number }> = [];
    let starting = 0;
    let benched = 0;
    let leaguesWithPlayers = 0;
    for (const lg of data.leagues) {
      if (hidden.has(lg.league_id)) continue;
      const ps = playersFor(lg, teams, data.meta);
      const st = ps.filter((p) => p.kind === 'start').length;
      starting += st;
      benched += ps.length - st;
      if (ps.length) leaguesWithPlayers++;
      if (hideEmpty && !ps.length) continue;
      rows.push({ lg, ps, st });
    }
    const totalVisible = data.leagues.filter((l) => !hidden.has(l.league_id)).length;
    return { games, week, rows, starting, benched, leaguesWithPlayers, totalVisible };
  }, [data, date, hidden, hideEmpty]);

  /* ----- guards ----- */
  if (profileLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }
  if (profileError || !account) {
    return <Alert severity="error">Sleeper account not found.</Alert>;
  }
  if (!isOwner) {
    return (
      <Alert
        severity="warning"
        action={
          <Button component={RouterLink} to={`/sleeper/${sleeperUserId}`} size="small">
            Back to profile
          </Button>
        }
      >
        Lineup Help is only available to the verified owner of this Sleeper account.
        {!user?.isVerified && (
          <>
            {' '}
            <Link component={RouterLink} to="/dashboard?verify=1">
              Verify your account
            </Link>{' '}
            to use it.
          </>
        )}
      </Alert>
    );
  }

  const withRoster = data ? data.leagues.filter((l) => l.roster).length : 0;
  const dataLabel = data ? `updated ${new Date(data.fetchedAt).toLocaleString()}` : '';

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            Lineup Help
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to={`/sleeper/${sleeperUserId}`} underline="hover">
              {account.current_username}
            </Link>
            {data
              ? ` · ${data.season} season · ${data.leagues.length} leagues (${withRoster} with your roster) · ${dataLabel}`
              : ' · loading your leagues from Sleeper…'}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography
            variant="caption"
            sx={{ maxWidth: 320, color: statusIsError ? 'error.main' : 'text.secondary' }}
          >
            {status}
          </Typography>
          <Button
            variant="contained"
            onClick={() => void refresh()}
            disabled={refreshing}
            startIcon={refreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
        </Stack>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Playing today" />
        <Tab label="Leagues" />
      </Tabs>

      {!data && (
        <Box display="flex" alignItems="center" gap={2} py={6} justifyContent="center">
          <CircularProgress size={20} />
          <Typography color="text.secondary">{status || 'Loading…'}</Typography>
        </Box>
      )}

      {data && view && tab === 0 && (
        <Box>
          {/* Controls */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" mb={2}>
            <TextField
              type="date"
              size="small"
              label="Date"
              value={date}
              onChange={(e) => e.target.value && setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDate(shiftDate(date, -1))}
              startIcon={<ArrowBackIcon />}
            >
              Prev
            </Button>
            <Button size="small" variant="outlined" onClick={() => setDate(isoLocal(new Date()))}>
              Today
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setDate(shiftDate(date, 1))}
              endIcon={<ArrowForwardIcon />}
            >
              Next
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={hideEmpty}
                  onChange={(e) => setHideEmpty(e.target.checked)}
                />
              }
              label="Hide leagues with nobody playing"
              sx={{ ml: 1 }}
            />
          </Stack>

          {/* Games */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={2}>
            {view.games.length ? (
              view.games.map((g, i) => (
                <Chip key={i} label={`${g.away} @ ${g.home}`} size="small" variant="outlined" />
              ))
            ) : (
              <Chip
                label={`No NFL games on ${prettyDate(date)}`}
                size="small"
                variant="outlined"
                sx={{ fontStyle: 'italic', color: 'text.secondary' }}
              />
            )}
          </Stack>

          {/* KPIs */}
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap mb={2}>
            <Kpi value={view.games.length} label={`games${view.week ? ` (week ${view.week})` : ''}`} />
            <Kpi value={view.rows.length} label="leagues shown" />
            <Kpi value={view.starting} label="starters playing" />
            <Kpi value={view.benched} label="bench / IR / taxi playing" />
          </Stack>

          {/* Table */}
          <TableContainer component={Card} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '30%', minWidth: 200 }}>League</TableCell>
                  <TableCell>My players playing</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {view.rows.map(({ lg, ps, st }) => {
                  const empty = emptyStarterSlots(lg);
                  const injured = ps.filter(
                    (p) => p.kind === 'start' && p.m.inj && p.m.inj !== 'Questionable'
                  ).length;
                  return (
                    <TableRow key={lg.league_id} sx={{ verticalAlign: 'top' }}>
                      <TableCell>
                        <Link
                          href={`https://sleeper.com/leagues/${lg.league_id}`}
                          target="_blank"
                          rel="noopener"
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                        >
                          {lg.name}
                        </Link>
                        {lg.roster && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {st} starting · {ps.length - st} not starting
                          </Typography>
                        )}
                        {empty > 0 && (
                          <Chip
                            icon={<WarningIcon />}
                            label={`${empty} empty starting slot${empty === 1 ? '' : 's'}`}
                            size="small"
                            color="warning"
                            sx={{ mt: 0.5, mr: 0.5 }}
                          />
                        )}
                        {injured > 0 && (
                          <Chip
                            icon={<WarningIcon />}
                            label={`${injured} starter${injured === 1 ? '' : 's'} flagged Out/Doubtful/IR`}
                            size="small"
                            color="warning"
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {!lg.roster ? (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            no roster in this league
                          </Typography>
                        ) : !ps.length ? (
                          <Typography variant="body2" color="text.secondary" fontStyle="italic">
                            none of your players play on this date
                          </Typography>
                        ) : (
                          <Stack spacing={0.5}>
                            {ps.map((p) => {
                              const dim = p.kind !== 'start';
                              return (
                                <Box
                                  key={p.pid}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    whiteSpace: 'nowrap',
                                    color: dim ? 'text.secondary' : 'text.primary',
                                  }}
                                >
                                  <SlotChip slot={p.slot} kind={p.kind} />
                                  <PosChip pos={p.m.pos} dim={dim} />
                                  <span>{p.m.name}</span>
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ minWidth: 32 }}
                                  >
                                    {p.m.team}
                                  </Typography>
                                  {p.m.inj && (
                                    <Tooltip title={p.m.inj}>
                                      <Box
                                        component="span"
                                        sx={{
                                          px: 0.75,
                                          borderRadius: 1,
                                          fontSize: '0.7rem',
                                          fontWeight: 700,
                                          bgcolor: theme.palette.error.main + '22',
                                          color: 'error.main',
                                        }}
                                      >
                                        {injAbbr(p.m.inj)}
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!view.rows.length && (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {hideEmpty
                          ? 'None of your visible leagues have players in action on this date.'
                          : 'No leagues selected — tick some on the Leagues tab.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {prettyDate(date)}: {view.leaguesWithPlayers} of {view.totalVisible} visible leagues
            have players in action
            {hideEmpty ? ' (leagues with nobody playing are hidden)' : ''}. Starters are
            highlighted; bench, taxi and IR players are greyed. Lineup data {dataLabel}.
          </Typography>
        </Box>
      )}

      {data && tab === 1 && (
        <Box>
          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            alignItems="center"
            mb={2}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 320px' }}>
              Untick a league to hide it from the "Playing today" tab. Order matches your
              Sleeper league list. Choices are saved in this browser.
            </Typography>
            <Button size="small" variant="outlined" onClick={() => persistHidden(new Set())}>
              Select all
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                persistHidden(
                  new Set(data.leagues.filter((l) => !l.roster).map((l) => l.league_id))
                )
              }
            >
              Only leagues with my roster
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => persistHidden(new Set(data.leagues.map((l) => l.league_id)))}
            >
              Select none
            </Button>
          </Stack>

          <Card variant="outlined">
            {data.leagues.map((lg, i) => (
              <Box
                key={lg.league_id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 1.5,
                  py: 0.5,
                  borderBottom: i < data.leagues.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  color: lg.roster ? 'text.primary' : 'text.secondary',
                }}
              >
                <Checkbox
                  size="small"
                  checked={!hidden.has(lg.league_id)}
                  onChange={(e) => {
                    const next = new Set(hidden);
                    if (e.target.checked) next.delete(lg.league_id);
                    else next.add(lg.league_id);
                    persistHidden(next);
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ minWidth: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                >
                  {i + 1}
                </Typography>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {lg.name}
                  {!lg.roster && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      fontStyle="italic"
                    >
                      {' '}
                      — no roster of yours in this league
                    </Typography>
                  )}
                </Typography>
              </Box>
            ))}
          </Card>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {data.leagues.filter((l) => !hidden.has(l.league_id)).length} of{' '}
            {data.leagues.length} leagues shown on the
            "Playing today" tab.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
